import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: false,
});

// Token interceptor + sucursal selector
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("mb_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  const sid = localStorage.getItem("mb_selected_sucursal");
  if (sid) config.headers["X-Sucursal-Id"] = sid;
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      const path = window.location.pathname;
      if (!path.startsWith("/login") && !path.startsWith("/pedido")) {
        localStorage.removeItem("mb_token");
        localStorage.removeItem("mb_user");
        window.location.href = "/login";
      }
    }
    return Promise.reject(err);
  }
);

export function formatApiError(err) {
  const d = err?.response?.data?.detail;
  if (!d) return err?.message || "Error desconocido";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(d);
}

export function fmtMXN(n) {
  return new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(Number(n) || 0);
}

export function fmtDate(iso) {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString("es-MX", {
      timeZone: "America/Mexico_City",
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export function fmtTime(iso) {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleTimeString("es-MX", {
      timeZone: "America/Mexico_City",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

/** Solo fecha en formato dd/MM/yyyy (zona horaria CDMX). */
export function fmtDateOnly(iso) {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleDateString("es-MX", {
      timeZone: "America/Mexico_City",
      day: "2-digit", month: "2-digit", year: "numeric",
    });
  } catch {
    return iso;
  }
}

export function elapsedMin(iso) {
  if (!iso) return 0;
  const ms = Date.now() - new Date(iso).getTime();
  return Math.max(0, Math.floor(ms / 60000));
}

/** Tiempo transcurrido formateado: segundos al inicio (<60s) y minutos después. */
export function elapsedLabel(iso) {
  if (!iso) return "0s";
  const ms = Math.max(0, Date.now() - new Date(iso).getTime());
  if (ms < 60000) return `${Math.floor(ms / 1000)}s`;
  const min = Math.floor(ms / 60000);
  if (min < 60) return `${min}m`;
  const h = Math.floor(min / 60);
  const rem = min % 60;
  return rem ? `${h}h ${rem}m` : `${h}h`;
}

// ============================================================================
// Reglas centrales de estado de orden (fuente única de verdad para frontend)
// ============================================================================
// Una orden es FINALIZADA cuando AMBAS condiciones son verdaderas:
//   1. Está cobrada (o.cobrada === true)
//   2. Todos los items activos (no CANCELADO ni DEVUELTO) están ENTREGADO
// Solo entonces desaparece de TODOS los módulos operativos.

/**
 * Devuelve true si TODOS los items activos están en estado ENTREGADO.
 * Items CANCELADO/DEVUELTO se ignoran. Items con `enviado=false` (todavía en
 * el carrito del mesero) también se consideran activos pendientes.
 */
export function entregaCompleta(orden) {
  if (!orden?.items?.length) return true;
  const activos = orden.items.filter((i) => !["CANCELADO", "DEVUELTO"].includes(i.estado));
  if (activos.length === 0) return true;
  return activos.every((i) => i.estado === "ENTREGADO");
}

/**
 * FINALIZADA = cobrada AND todo entregado AND no anulada.
 * Esta orden ya cumplió su ciclo operativo completo.
 */
export function ordenFinalizada(orden) {
  if (!orden) return false;
  if (["CANCELADA", "DEVUELTA"].includes(orden.estado)) return true; // anuladas también terminan
  return !!orden.cobrada && entregaCompleta(orden);
}

/**
 * Activa para mesero/salón: cualquier orden no finalizada.
 */
export function ordenActiva(orden) {
  return !ordenFinalizada(orden);
}

/**
 * Activa para caja (pendiente de cobro): no cobrada y no cancelada.
 */
export function ordenPendienteCobro(orden) {
  if (!orden) return false;
  if (["CANCELADA", "DEVUELTA"].includes(orden.estado)) return false;
  return !orden.cobrada;
}

